import { WATCHLIST_CONSTS as WC } from '../consts'
import services from '@/services/ascentiaServiceIndex'
import moment from 'moment'
import _ from 'lodash'

const state = {
  riskScoresByTail: [],
  riskScoreFriendlyNames: [],
  watchlistFilters: {
    operators: [],
    platforms: [],
    tails: [],
    analytics: [],
  },
  riskScoreList: [],
  featureList: [],
  watchlistDrilldownSelections: {
    tail: null,
    aircraftId: null,
    lastUpdated: null,
    riskScoreKey: null,
    riskScoreName: null,
    riskScoreValue: null,
    fleetAverage: null,
    airlineId: null,
    featureKey: null,
  },
  drilldownSelectors: [],
  selectorsWithData: [],
  alertValues: [],
}

const getters = {
  watchlistFiltersSelected: state => {
    return state.watchlistFilters
  },
  filteredRiskScores: state => {
    if (!state.riskScoresByTail || state.riskScoreFriendlyNames.length === 0) {
      return []
    }
    return state.riskScoresByTail.flatMap(tailRisk =>
      tailRisk.riskSummaries.map(riskSummary => {
        return {
          ...riskSummary,
          tailNumber: tailRisk.tailNumber,
          mostRecentFlight: tailRisk.mostRecentFlight,
          idColumn: {
            value: `${tailRisk.tailNumber}_${riskSummary.scoreName}`,
          },
        }
      }),
    )
  },
  currentRiskScore: state => {
    return state.riskScoreList.find(
      riskScore =>
        riskScore.riskScoreKey ===
        state.watchlistDrilldownSelections.riskScoreKey,
    )
  },
}

const mutations = {
  [WC.UPDATE_RISK_SCORES_BY_TAIL]: (state, data) =>
    (state.riskScoresByTail = data),
  [WC.ADD_RISK_SCORES_BY_TAIL]: (state, data) =>
    (state.riskScoresByTail = _.uniqBy(
      state.riskScoresByTail.concat(data),
      item => item.tailNumber,
    )),
  [WC.UPDATE_RISK_SCORE_FRIENDLY_NAMES]: (state, data) =>
    (state.riskScoreFriendlyNames = data),
  [WC.ADD_RISK_SCORE_FRIENDLY_NAMES]: (state, data) =>
    (state.riskScoreFriendlyNames = state.riskScoreFriendlyNames.concat(data)),
  [WC.UPDATE_WATCHLIST_FILTERS]: (state, data) =>
    (state.watchlistFilters = data),
  [WC.UPDATE_PHM_ALERT_VALUES]: (state, data) => (state.alertValues = data),
  [WC.UPDATE_WATCHLIST_DRILLDOWN_SELECTION]: (state, data) =>
    (state.watchlistDrilldownSelections = data),
  [WC.UPDATE_WATCHLIST_RISK_SCORE_LIST]: (state, data) =>
    (state.riskScoreList = data),
  [WC.UPDATE_WATCHLIST_FEATURE_LIST]: (state, data) =>
    (state.featureList = data),
  [WC.UPDATE_FEATURE_DATA](state, selector) {
    const idx = state.selectorsWithData.map(sel => sel.id).indexOf(selector.id)
    state.selectorsWithData.splice(idx, 1, selector)
  },
  [WC.ADD_SELECTOR](state, selector) {
    selector.data = []
    selector.showInNavigator = false
    selector.visible = false
    selector.showInLegend = false
    state.selectorsWithData.push(selector)
  },
  [WC.CLEAR_SELECTORS]: state => (state.selectorsWithData = []),
  [WC.CLEAR_ALERT_VALUES]: state => (state.alertValues = []),
  [WC.UPDATE_SELECTOR](state, selector) {
    const idx = state.selectorsWithData.map(sel => sel.id).indexOf(selector.id)
    state.selectorsWithData.splice(idx, 1, selector)
  },
  [WC.REMOVE_SELECTOR](state, id) {
    const idx = state.selectorsWithData.map(sel => sel.id).indexOf(id)
    state.selectorsWithData.splice(idx, 1)
  },
  [WC.HIDE_SELECTOR](state, id) {
    const idx = state.selectorsWithData.map(sel => sel.id).indexOf(id)
    const selector = state.selectorsWithData[idx]
    selector.hidden = !selector.hidden
    selector.visible = !selector.visible
    state.selectorsWithData.splice(idx, 1, selector)
  },
}

const actions = {
  async [WC.GET_ALL_RISK_SCORES_BY_TAIL]({ commit }, selectedAirlines) {
    await Promise.all([
      services.analytic.getData('allRiskScoresByTail', selectedAirlines, true),
    ])
      .then(response => {
        const tailRisks = response[0].flatMap(result => {
          return result &&
            result.status &&
            result.status === 200 &&
            result.data &&
            result.data.tailRisks
            ? result.data.tailRisks
            : []
        })
        commit(
          tailRisks.length
            ? WC.ADD_RISK_SCORES_BY_TAIL
            : WC.UPDATE_RISK_SCORES_BY_TAIL,
          tailRisks,
        )
      })
      .catch(error =>
        console.error(WC.ADD_ALL_RISK_SCORES_BY_TAIL + ': error ' + error),
      )
  },

  async [WC.GET_RISK_SCORE_FRIENDLY_NAMES]({ commit }, airlineId) {
    try {
      const response = await services.analytic.getData(
        'riskScoreFriendlyNames',
        {
          id: airlineId,
        },
      )
      if (response && response.status && response.status === 200) {
        const namesObj = response.data
        const namesArr = Object.keys(namesObj).map(item => ({
          parentId: airlineId,
          value: item,
          text: namesObj[item],
        }))
        commit(WC.ADD_RISK_SCORE_FRIENDLY_NAMES, namesArr)
      }
    } catch (error) {
      console.error(WC.GET_RISK_SCORE_FRIENDLY_NAMES + ': error' + error)
    }
  },

  async [WC.GET_WATCHLIST_RISK_SCORE_LIST]({ commit }) {
    try {
      const response = await services.analytic.getData(
        'watchlistRiskScoresFeatures',
      )
      if (response && response.status && response.status === 200) {
        commit(WC.UPDATE_WATCHLIST_RISK_SCORE_LIST, response.data)
      }
    } catch (error) {
      console.error(WC.GET_WATCHLIST_RISK_SCORE_LIST + ': error' + error)
    }
  },

  async [WC.GET_WATCHLIST_FEATURE_LIST]({ commit }) {
    try {
      const response = await services.analytic.getData('watchlistFeatures')
      if (response && response.status && response.status === 200) {
        response.data.sort((a, b) => {
          return a.name < b.name ? -1 : 1
        })
        commit(WC.UPDATE_WATCHLIST_FEATURE_LIST, response.data)
      }
    } catch (error) {
      console.error(WC.GET_WATCHLIST_FEATURE_LIST + ': error' + error)
    }
  },

  async [WC.GET_FEATURE_DATA]({ commit }, selector) {
    try {
      const response = await services.analytic.getData(
        'analyticResults',
        selector,
        true,
      )
      if (response && response.status && response.status === 200) {
        const cleanedData = response.data
          .filter(
            item => (item.point || item.point === 0) && !isNaN(item.point),
          )
          .map(item => [
            +moment.utc(item.timestamp, 'YYYY-MM-DD HH:mm:ss.SSS'),
            item.point,
            item.flightNumber,
          ])
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : 1
          })

        selector.data = cleanedData
        selector.visible = true
        selector.showInLegend = true
        selector.showInNavigator = true
        commit(WC.UPDATE_FEATURE_DATA, selector)
      }
    } catch (error) {
      console.error(WC.GET_FEATURE_DATA + ': error ' + error)
    }
  },

  async [WC.GET_PHM_ALERTS]({ commit, state }) {
    try {
      const startDate = moment.utc().add(-30, 'days').format('YYYY-MM-DD')
      const options = {
        tail: state.watchlistDrilldownSelections.tail,
        startDate: startDate,
        airlineId: state.watchlistDrilldownSelections.airlineId,
        id:
          state.watchlistDrilldownSelections.tail +
          '_' +
          state.watchlistDrilldownSelections.riskScoreKey,
      }
      const alertNames = state.riskScoreList.find(
        rs =>
          rs.riskScoreKey === state.watchlistDrilldownSelections.riskScoreKey,
      ).alerts
      const trueAlerts = await Promise.all(
        alertNames.map(async alert => {
          options.alertKey = alert.alertKey
          options.id =
            options.tail + '_' + alert.alertKey + '_' + options.startDate
          const response = await services.analytic.getData(
            'alertValues',
            options,
            true,
          )
          const ret = {
            displayName: alert.displayName,
            alertKey: alert.alertKey,
            alertDates: response.data.alerts
              .filter(alert => alert.alertValue > 0)
              .map(alert => alert.timestamp),
          }
          return ret
        }),
      )
      commit(WC.UPDATE_PHM_ALERT_VALUES, trueAlerts)
    } catch (error) {
      console.error(WC.GET_PHM_ALERTS + ': error ' + error)
      console.error('Confirm that this analytic is in the Ascentia database')
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
